// utils/plusStatus.js
//
// What to say about a device's Plus state. Pure, so the wording is tested
// (plusStatus.test.js) and the Alerts card and the admin page agree.
//
// Input is devices/{id}.plus as the app reads it (Firestore Timestamps) or as
// adminOverview returns it (…Ms numbers). Written by the cloud only:
//   { comped, entitled, status, plan, trialEndsAt, renewsAt, endsAt,
//     cancelAtPeriodEnd, paymentFailed, includedYearUsed }

const TZ = 'Pacific/Honolulu';

export const PLAN_PRICE = { yearly: '$50/year', monthly: '$5/month' };

/**
 * When an included year activated now would end: noon Hawaii time on the same
 * date next year. Must match billing.includedEndMs in the cloud, which sets
 * Stripe's trial_end, so the app and Stripe show the same day.
 */
export function includedEndsMs(nowMs = Date.now()) {
  const hst = new Date(nowMs - 10 * 3600 * 1000); // Hawaii has no DST
  return Date.UTC(hst.getUTCFullYear() + 1, hst.getUTCMonth(), hst.getUTCDate(), 22, 0, 0);
}

function toMs(v) {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return null;
}

export function fmtPlusDate(ms) {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('en-US', {
    timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric',
  });
}

/** Normalized view of the stored state, either shape. */
export function readPlus(p) {
  const x = p || {};
  return {
    comped: x.comped === true,
    entitled: x.entitled === true,
    status: x.status || null,
    plan: x.plan || null,
    nextPlan: x.nextPlan || null,
    trialEndsAtMs: toMs(x.trialEndsAt) ?? toMs(x.trialEndsAtMs),
    renewsAtMs: toMs(x.renewsAt) ?? toMs(x.renewsAtMs),
    endsAtMs: toMs(x.endsAt) ?? toMs(x.endsAtMs),
    cancelAtPeriodEnd: x.cancelAtPeriodEnd === true,
    paymentFailed: x.paymentFailed === true,
    includedYearUsed: x.includedYearUsed === true,
    startedFromFreeAtMs: toMs(x.startedFromFreeAt) ?? toMs(x.startedFromFreeAtMs),
  };
}

/**
 * The "SoKo Plus is on" screen at setup, for a monitor whose included year
 * just started (bought on the website: it starts by itself). null when that
 * isn't the situation (comped, already paying, won't renew, or not on).
 */
export function plusStartedNotice(p) {
  const v = readPlus(p);
  if (v.comped || !v.entitled || v.status !== 'trialing' || v.cancelAtPeriodEnd) return null;
  const until = fmtPlusDate(v.trialEndsAtMs || v.renewsAtMs);
  const price = PLAN_PRICE[v.nextPlan || v.plan] || PLAN_PRICE.yearly;
  return {
    title: 'SoKo Plus is on',
    lines: [
      until ? `Your first year is included, until ${until}.` : 'Your first year is included.',
      `After that, SoKo Plus renews at ${price} unless you cancel. Change or cancel anytime in Settings → SoKo Plus.`,
    ],
  };
}

/** Plus is on: comped, or Stripe says so. */
export function plusIsOn(p) {
  const v = readPlus(p);
  return v.comped || v.entitled;
}

/**
 * { kind, title, line, action }
 *   kind:   'comped' | 'trial' | 'active' | 'payment-failed' | 'none'
 *   action: 'start' | 'manage' | 'update-card' | null  (null for shared users)
 */
export function plusStatus(p, { isOwner = true } = {}) {
  const v = readPlus(p);
  const price = PLAN_PRICE[v.plan] || PLAN_PRICE.yearly;
  // The next renewal charges the scheduled plan if they switched (stored by
  // the cloud from Stripe), else the current one. Never a guess.
  const nextPrice = PLAN_PRICE[v.nextPlan || v.plan] || PLAN_PRICE.yearly;
  const manage = isOwner ? 'manage' : null;

  if (v.comped) {
    return { kind: 'comped', title: 'SoKo Plus active', line: 'Included at no charge.', action: null };
  }

  if (v.entitled && v.paymentFailed) {
    return {
      kind: 'payment-failed',
      title: "Payment didn't go through",
      line: isOwner ? 'Update your card to keep SoKo Plus.' : "SoKo Plus will end unless the owner updates their card.",
      action: isOwner ? 'update-card' : null,
    };
  }

  if (v.entitled && v.status === 'trialing') {
    const until = fmtPlusDate(v.trialEndsAtMs || v.endsAtMs || v.renewsAtMs);
    let line = 'Renews automatically.';
    if (v.cancelAtPeriodEnd) line = until ? `Ends ${until}. It won't renew.` : "It won't renew.";
    else if (until) line = `Renews ${until} at ${nextPrice}.`;
    return { kind: 'trial', title: 'SoKo Plus active · First year included', line, action: manage };
  }

  if (v.entitled) {
    if (v.cancelAtPeriodEnd) {
      const ends = fmtPlusDate(v.endsAtMs || v.renewsAtMs);
      return {
        kind: 'active',
        title: 'SoKo Plus active',
        line: ends ? `Ends ${ends}. It won't renew.` : "It won't renew.",
        action: manage,
      };
    }
    const renews = fmtPlusDate(v.renewsAtMs);
    return {
      kind: 'active',
      title: `SoKo Plus active · ${price}`,
      line: renews ? `Renews ${renews} at ${nextPrice}.` : 'Renews automatically.',
      action: manage,
    };
  }

  if (!isOwner) {
    return {
      kind: 'none',
      title: 'Free plan',
      line: "This monitor doesn't have SoKo Plus. The owner can turn it on in Settings.",
      action: null,
    };
  }
  // The first year comes with the monitor: they're activating something they
  // already have, not buying it. Once used, it's a plain start.
  return v.includedYearUsed
    ? { kind: 'none', title: 'SoKo Plus', line: `${PLAN_PRICE.yearly} or ${PLAN_PRICE.monthly}.`, action: 'start', button: 'Start SoKo Plus' }
    : { kind: 'none', title: 'Your first year of SoKo Plus is included', line: "Add a card to activate it. You won't be charged for a year.", action: 'start', button: 'Activate SoKo Plus' };
}

/** Eligible for the "activate your included year" prompts (owner only). */
export function canActivateIncluded(p) {
  const v = readPlus(p);
  return !v.comped && !v.entitled && !v.includedYearUsed;
}

/**
 * Line under the button: what's charged today, the renewal price they picked
 * and when, and how to cancel. Stripe Checkout repeats it above Subscribe.
 */
export function startDisclosure({ includedYearUsed, plan = 'yearly', nowMs = Date.now() }) {
  const price = PLAN_PRICE[plan] || PLAN_PRICE.yearly;
  return includedYearUsed
    ? `Charged today. SoKo Plus renews at ${price} unless you cancel. Cancel anytime in the app.`
    : `$0 today. SoKo Plus renews at ${price} on ${fmtPlusDate(includedEndsMs(nowMs))} unless you cancel. `
      + 'Cancel anytime in the app.';
}

/** Short label for the admin list chip. */
export function plusChip(p) {
  const v = readPlus(p);
  if (v.comped) return 'Comped';
  if (v.entitled && v.paymentFailed) return 'Past due';
  if (v.entitled && v.status === 'trialing') return 'Plus · trial';
  if (v.entitled) return 'Plus';
  return null;
}

/** One line for the admin device panel: how this device has Plus, if at all. */
export function plusAdminLine(p) {
  const v = readPlus(p);
  if (v.comped) return 'Comped (no charge)';
  if (!v.status) return 'No subscription';
  const plan = v.plan ? ` · ${PLAN_PRICE[v.plan]}` : '';
  if (v.entitled && v.paymentFailed) return `Payment failed, Stripe retrying${plan}`;
  if (v.entitled && v.status === 'trialing') {
    return `Included year until ${fmtPlusDate(v.trialEndsAtMs)}${plan}${v.cancelAtPeriodEnd ? ' · won\'t renew' : ''}`;
  }
  if (v.entitled) {
    return v.cancelAtPeriodEnd
      ? `Paid${plan} · ends ${fmtPlusDate(v.endsAtMs)}`
      : `Paid${plan} · renews ${fmtPlusDate(v.renewsAtMs)}`;
  }
  return `Ended (${v.status})`;
}

/**
 * The small status chip beside the monitor pills (Alerts).
 *   { label, tone: 'on' | 'warn' | 'off' }
 */
export function plusBadge(p) {
  const v = readPlus(p);
  if (v.entitled && v.paymentFailed && !v.comped) return { label: 'Payment failed', tone: 'warn' };
  if (v.comped || v.entitled) return { label: 'SoKo Plus active', tone: 'on' };
  return { label: 'Free plan', tone: 'off' };
}

// ── Free tier (mirrors billing.tierOf in the cloud) ──────────────────────
// A claimed monitor without Plus is on Free once its first 24 hours after
// claiming are up: hourly reporting, a once-a-day view (06:00 HST), one
// low-water email. The cloud enforces it; this only decides what to show.
export const FREE_GRACE_MS = 24 * 60 * 60 * 1000;
export const FREE_CADENCE_SEC = 3600;
export const FREE_LOW_OPTIONS = [
  { key: 'low30', value: 30 },
  { key: 'low20', value: 20 },
  { key: 'low10', value: 10 },
];

/** 'plus' | 'free-grace' | 'free' | 'unclaimed' */
export function tierOf(dev, nowMs = Date.now()) {
  const d = dev || {};
  if (plusIsOn(d.plus)) return 'plus';
  if (d.claimed === false) return 'unclaimed';
  const claimedMs = toMs(d.claimedAt);
  if (claimedMs && nowMs - claimedMs < FREE_GRACE_MS) return 'free-grace';
  return 'free';
}

// ── Online / offline (mirrors billing.offlineAfterMs in the cloud) ────────
// Offline after 5 missed reports at the interval the monitor is on, never
// sooner than 2 minutes. When SoKo Plus starts on a monitor that was on
// Free's hourly reporting, the monitor only learns its new interval at its
// next check-in (up to an hour), so until it reports (or 65 minutes pass)
// the hourly interval still applies.
export const LIVE_SWITCH_MS = 65 * 60 * 1000;

/** SoKo Plus just started on an hourly monitor that hasn't reported since. */
export function liveSwitchPending(plus, lastReportMs, nowMs = Date.now()) {
  const at = readPlus(plus).startedFromFreeAtMs;
  if (!at || nowMs - at >= LIVE_SWITCH_MS) return false;
  return !(typeof lastReportMs === 'number' && lastReportMs > at);
}

/** Seconds between reports, as the monitor runs now. */
export function reportIntervalSec(dev, lastReportMs, nowMs = Date.now()) {
  const d = dev || {};
  if (tierOf(d, nowMs) === 'free' || liveSwitchPending(d.plus, lastReportMs, nowMs)) return FREE_CADENCE_SEC;
  return (typeof d.steadyPollSec === 'number' && d.steadyPollSec > 0) ? d.steadyPollSec : 30;
}

/** How long without a report before the monitor counts as offline. */
export function offlineAfterMs(dev, lastReportMs, nowMs = Date.now()) {
  return Math.max(2 * 60 * 1000, 5 * reportIntervalSec(dev, lastReportMs, nowMs) * 1000);
}

/** The Free low-water email level they chose, or null (off). */
export function freeLowOf(alerts) {
  const k = alerts && alerts.freeLow;
  return FREE_LOW_OPTIONS.find((o) => o.key === k) || null;
}
